// Обёртка элемента в канвасе — порт ref SortableSubBlockItem (DesignBlockEditor.tsx:833-877):
// выделение рингом, паддинг по настройке (кроме типов из SELF_PADDED), на hover — ручка слева
// снизу и корзина справа снизу.
//
// Тема приложения — тёмная: app-токены (ring-primary, text-foreground, border-border,
// bg-foreground/5) на канвасе блока либо не видны, либо полностью прозрачны. Вся
// хром-графика этого компонента — выделение, hover-подсветка, цвет ручки — идёт из
// blockTheme, инлайновым стилем, а не классом.
//
// Кольцо выделения — `outline` с отступом, а НЕ двойная тень с цветной прослойкой
// (было `boxShadow: 0 0 0 2px <прослойка>, 0 0 0 4px <primary>`). Прослойку красили
// blockTheme.background — годилось, пока канвас блока был всегда белым; теперь у
// блока три темы (кремовая/персиковая/лавандовая), и белая прослойка на персиковом
// или лавандовом фоне видна как чужеродный ореол в 2px. `outline-offset` вместо
// закрашенной прослойки оставляет зазор прозрачным — он просто показывает
// реальный фон под собой, красить нечего.
import { useMessages } from '@/i18n'
import { messages } from './messages'
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { GripVertical, Trash2 } from 'lucide-react'
import { useDndContext } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { blockTheme, cn, hsl } from '@teach/ui'
import type { DesignElement } from '@/types/blocks'
import { paddingClass, SELF_PADDED } from '@/features/editor/lib/designElementStyles'
import { ToolbarBelowContext, ToolbarFreezeContext } from './quickToolbarContext'
import { useDesignToolbarSuppressed } from '@/features/editor/lib/designToolbarSuppressed'

interface Props {
  element: DesignElement
  editable: boolean
  isSelected: boolean
  onSelect: () => void
  onDelete: () => void
  /** Быстрые настройки над выделенным элементом (`DesignQuickToolbar`). */
  toolbar?: ReactNode
  /** Первый элемент: над ним нет места — панель встаёт под ним. */
  toolbarBelow?: boolean
  children: ReactNode
}

/** Сколько нужно над элементом под панель и раскрытый вверх список (5 пунктов). */
const TOOLBAR_ROOM_PX = 330

export const SortableDesignElement = ({
  element,
  editable,
  isSelected,
  onSelect,
  onDelete,
  toolbar,
  toolbarBelow = false,
  children,
}: Props) => {
  const m = useMessages(messages)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: element.id,
    disabled: !editable,
  })
  // Правило рендера конкретного типа обёртка не знает — спрашивает реестр.
  const skipPadding = SELF_PADDED.has(element.type)
  const [hovered, setHovered] = useState(false)
  const dragging = useDndContext().active !== null
  const toolbarSuppressed = useDesignToolbarSuppressed()
  // Позиция панели, замороженная на время открытого списка (`ToolbarFreezeContext`).
  const toolbarRef = useRef<HTMLDivElement>(null)
  const [frozenTop, setFrozenTop] = useState<number | null>(null)
  const freeze = useCallback((frozen: boolean) => {
    setFrozenTop(frozen && toolbarRef.current ? toolbarRef.current.offsetTop : null)
  }, [])

  // Тумбнейлы таймлайна рендерят тот же блок read-only с теми же id элементов —
  // без гейта по editable выделение в большом канвасе просачивалось бы в тумбнейл.
  const showRing = editable && isSelected
  // Над элементом мало места (у верха холста полоска прогресса) — панель встаёт
  // ПОД него: списки раскрываются от элемента и тогда помещаются, а не ложатся
  // на текст. Считается в момент выделения, а не на каждый кадр.
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [roomyAbove, setRoomyAbove] = useState(true)
  useLayoutEffect(() => {
    if (!showRing || !rootRef.current) return
    const el = rootRef.current.getBoundingClientRect()
    const canvas = rootRef.current.closest('[data-design-canvas]')?.getBoundingClientRect()
    setRoomyAbove(!canvas || el.top - canvas.top >= TOOLBAR_ROOM_PX)
  }, [showRing])
  const placeBelow = toolbarBelow || !roomyAbove
  const style: CSSProperties = {
    // Только сдвиг, без масштаба: Transform растягивал элемент под высоту соседа.
    transform: CSS.Translate.toString(transform),
    transition,
    // Выделение — слабо заметная фиолетовая рамка (яркая 2px шумела, заказчик);
    // наведение на НЕвыделенный — тонкий пунктир (заливка шумела поверх картинок).
    outline: showRing
      ? `1.5px solid ${hsl(blockTheme.primary, 0.35)}`
      : editable && hovered
        ? `1px dashed ${hsl(blockTheme.primary, 0.45)}`
        : undefined,
    outlineOffset: showRing || (editable && hovered) ? '2px' : undefined,
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        rootRef.current = node
      }}
      data-testid="design-element"
      style={style}
      onClick={(e) => {
        e.stopPropagation()
        onSelect()
      }}
      onMouseEnter={() => editable && setHovered(true)}
      onMouseLeave={() => editable && setHovered(false)}
      className={cn(
        'group relative w-full cursor-pointer',
        skipPadding ? null : paddingClass(element.padding),
        isDragging ? 'z-50 opacity-50' : null,
        editable ? 'rounded-lg transition-colors' : null,
      )}
    >
      {/* Пока что-то тащат — панели нет: она мешала бы видеть, куда встаёт элемент. */}
      {showRing && toolbar && !dragging && !toolbarSuppressed ? (
        <div
          ref={toolbarRef}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'absolute left-1/2 z-40 -translate-x-1/2 whitespace-nowrap duration-150 animate-in fade-in',
            frozenTop !== null ? null : placeBelow ? 'top-full mt-3' : 'bottom-full mb-3',
          )}
          // Заморожена — стоит на том месте, где её застал открытый список.
          style={frozenTop !== null ? { top: frozenTop } : undefined}
        >
          <ToolbarFreezeContext.Provider value={freeze}>
            <ToolbarBelowContext.Provider value={placeBelow}>
              <div className="flex items-center gap-0.5 rounded-xl bg-popover/95 p-1 text-popover-foreground shadow-xl ring-1 ring-border backdrop-blur">
                {toolbar}
              </div>
            </ToolbarBelowContext.Provider>
          </ToolbarFreezeContext.Provider>
        </div>
      ) : null}
      {children}
      {editable ? (
        // Ручка и корзина — по краям, наполовину за рамкой элемента и по центру его высоты:
        // так они не закрывают текст. Стеклянные — фон под ними размывается.
        // На касании наведения нет, а прозрачная корзина нажималась и удаляла без подтверждения:
        // там ручка и корзина есть только у выделенного элемента.
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 -left-3 -right-3 flex items-center justify-between opacity-0 transition-opacity group-hover:opacity-100',
            showRing ? '[@media(hover:none)]:opacity-100' : '[@media(hover:none)]:hidden',
          )}
        >
          <span
            {...attributes}
            {...listeners}
            aria-label={m.dragElement}
            className="pointer-events-auto cursor-grab rounded-full border p-1.5 shadow-md backdrop-blur-xl backdrop-saturate-150 transition hover:scale-110"
            style={{
              backgroundColor: hsl(blockTheme.background, 0.55),
              borderColor: hsl(blockTheme.background, 0.8),
            }}
          >
            <GripVertical
              className="h-3.5 w-3.5"
              style={{ color: hsl(blockTheme.foreground, 0.7) }}
            />
          </span>
          <button
            type="button"
            aria-label={m.removeElement}
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="pointer-events-auto rounded-full border p-1.5 shadow-md backdrop-blur-xl backdrop-saturate-150 transition hover:scale-110"
            style={{
              backgroundColor: hsl(blockTheme.background, 0.55),
              borderColor: hsl(blockTheme.background, 0.8),
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </button>
        </div>
      ) : null}
    </div>
  )
}
